import React, { useEffect } from 'react';
import { ContainerComponent } from '../../../core/components/container.component';
import { useTranslation } from 'react-i18next';
import { convertDateToCivil, convertDateToIslamic, convertDateToPersianSimple } from '../../../core/utils/DateUtils';
import { TextComponent } from '../../../core/components/base/textField.component';
import { RowComponent } from '../../../core/components/base/row.component';
import { RTLContainer } from '../../../core/components/RTLContainer.component';
import { ColumnComponent } from '../../../core/components/base/column.component';
import { BOTTOM_LONG_ARROW_URL } from '../../../page/tools/time_zone_difference/CONSTANTS';
import { toEnglishNumber, toPersianNumber } from '../../../core/utils/number.utils';
import { CalendarDrawer } from '../../../page/tools/time_zone_difference/components/calendar.drawer';
import { calculateConvertDate } from '../../../page/tools/date_converter/dateConverter.utils';
import { NextPage } from 'next';
import { TextField } from '@mui/material';

const DateConverter: NextPage = () => {
  const [activeStartDateSort, setActiveStartDateSort] = React.useState(0);
  const [activeEndDateSort, setActiveEndDateSort] = React.useState(0);
  const [startDateValue, setStartDateValue] = React.useState('');
  const [resultText, setResultText] = React.useState('');
  const [startCalendarOpen, setStartCalendarOpen] = React.useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    switch (activeStartDateSort) {
      case 0:
        setStartDateValue(convertDateToPersianSimple(new Date()));
        break;
      case 1:
        setStartDateValue(convertDateToIslamic(new Date()));
        break;
      case 2:
        setStartDateValue(convertDateToCivil(new Date()));
        break;
    }
  }, [activeStartDateSort]);

  const handleChangeStart = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStartDateValue(event.currentTarget.value);
  };

  function calculateDateConverter() {
    setResultText(calculateConvertDate(activeStartDateSort, activeEndDateSort, startDateValue));
  }

  useEffect(() => {
    calculateDateConverter();
  }, [startDateValue, activeStartDateSort, activeEndDateSort]);

  return (
    <ContainerComponent>
      <ColumnComponent className={'px-5 totalCenter relative'}>
        <TextComponent
          text={'engineering.dateSort'}
          size={'base'}
          isFromTranslate={true}
          className="mTextLighter p-2 mt-4 text-bold"
        />
        <RowComponent className="text-center justify-around md:w-2/3 lg-w-1/2">
          <div
            data-testid="persianStartType"
            className={`card p-2 m-2 width-auto grow ${
              activeStartDateSort === 0 ? 'card-charge' : 'card-charge-border'
            }`}
            onClick={() => setActiveStartDateSort(0)}
          >
            {t('engineering.shamsi')}
          </div>
          <div
            data-testid="islamicStartType"
            className={`card p-2 m-2 width-auto grow ${
              activeStartDateSort === 1 ? 'card-charge' : 'card-charge-border'
            }`}
            onClick={() => setActiveStartDateSort(1)}
          >
            {t('engineering.ghamari')}
          </div>
          <div
            data-testid="civilStartType"
            className={`card p-2 m-2 width-auto grow ${
              activeStartDateSort === 2 ? 'card-charge' : 'card-charge-border'
            }`}
            onClick={() => setActiveStartDateSort(2)}
          >
            {t('engineering.miladi')}
          </div>
        </RowComponent>
        <RTLContainer className="totalCenter mt-8 px-2">
          <TextField
            inputProps={{ 'data-testid': 'startDate', style: { textAlign: 'center' } }}
            onClick={() => setStartCalendarOpen(true)}
            style={{ marginTop: '1rem', textAlign: 'center' }}
            autoComplete="off"
            onChange={handleChangeStart}
            value={startDateValue}
            type={'text'}
            className="w-full md:w-1/2 lg:w-1/3 !mt-4 !text-center"
            label={t('engineering.startDate')}
            placeholder={t('engineering.startDate')}
            variant={'outlined'}
          />
        </RTLContainer>
        <RowComponent className="mt-6">
          <ColumnComponent className="items-start justify-center">
            <div
              data-testid="persianEndType"
              className={`card p-2 m-2 text-center !w-24 ${
                activeEndDateSort === 0 ? 'card-charge' : 'card-charge-border'
              }`}
              onClick={() => setActiveEndDateSort(0)}
            >
              {t('engineering.shamsi')}
            </div>
            <div
              data-testid="islamicEndType"
              className={`card p-2 m-2 text-center !w-24 ${
                activeEndDateSort === 1 ? 'card-charge' : 'card-charge-border'
              }`}
              onClick={() => setActiveEndDateSort(1)}
            >
              {t('engineering.ghamari')}
            </div>
            <div
              data-testid="civilEndType"
              className={`card p-2 m-2 text-center !w-24 ${
                activeEndDateSort === 2 ? 'card-charge' : 'card-charge-border'
              }`}
              onClick={() => setActiveEndDateSort(2)}
            >
              {t('engineering.miladi')}
            </div>
          </ColumnComponent>
          <img src={BOTTOM_LONG_ARROW_URL} className="max-h-60 px-5" alt={''} />
          <ColumnComponent className="totalCenter"></ColumnComponent>
        </RowComponent>
        <div data-testid="totalResultText" className="text-lg w-full mTextBlue text-center mt-10">
          {t('engineering.convertedDate')}
        </div>
        <div data-testid="totalResultText" className="text-2xl w-full text-center mt-2">
          {toPersianNumber(resultText)}
        </div>
        <CalendarDrawer
          initialDate={toEnglishNumber(startDateValue)}
          local={'fa'}
          calendarOpen={startCalendarOpen}
          type={activeStartDateSort}
          title={t('engineering.startDate')}
          calendarOpenFunction={(state: boolean) => setStartCalendarOpen(state)}
          calendarChoose={(year: string, month: string, day: string) => {
            setStartDateValue(toPersianNumber(year + '/' + (Number(month) + 1) + '/' + day));
            setStartCalendarOpen(false);
          }}
        />
      </ColumnComponent>
    </ContainerComponent>
  );
};

export default DateConverter;
